import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { OrdersType } from "../../../shared/interfaces/requests.interface";
import requestErrorHandler from "../../../util/requestErrorHandler";
import axiosConfig from "../../../util/axiosConfig";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Loader, Search } from "lucide-react";
import { useCurrency } from "../../../hooks/useCurrency";
import placeholder from "../../../util/placeholder";
import FleetOwners from "../../../components/request/FleetOwners";
import FleetOwnersAssigned from "../../../components/request/FleetOwnersAssigned";
import { REQUEST_STATUS } from "../../../util/constants";

const BASE_URL = process.env.REACT_APP_BACKEND_URL!;


export default function TransactionDetails() {
  const { requestId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [request, setRequest] = useState<OrdersType>();
  const {formatCurrency} = useCurrency();
    
  const getRequest = async () => {
    setIsLoading(true);
    try {
    const response = await axiosConfig.get(`${BASE_URL}/renter/rental-request/${requestId}`);
    const request = response?.data?.data;
    setRequest({...request, price: request.amountCharged
 || request.totalPrice});
    } catch (error) {
      requestErrorHandler(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getRequest();
  }, []);

  if (isLoading || !request) {
    return (<div className="h-96 flex items-center justify-center"><Loader /></div>)
  }

  return (
    <div className="flex flex-col md:flex-row gap-4 p-4 min-h-full">
      {/* Left Section */}
      <div className="flex-1 bg-white rounded-lg shadow p-6">
        <header className="border-solid border-transparent border-b border-b-[#1A237E]">
          <h1 className="text-blue-600 text-lg mb-4 flex items-center gap-2 font-bold">
            <ChevronLeft className="text-lg" onClick={() => window.history.back()} />
            <span className="font-bold">Transaction Details</span>
          </h1>
        </header>
        <div className="flex flex-col md:flex-row gap-6">
          {/* Rental Request Info */}
          <div className="flex-1 flex flex-col gap-2 min-w-[220px]">
            <h2 className="font-semibold text-base text-gray-500 my-4">Rental Request Information</h2>
            <Link to={"#"} className="text-blue-600 font-bold">
              {request.invoiceId}
            </Link>
            <p className="text-[#F9A825] font-semibold text-lg my-0">{formatCurrency(request.price)}</p>
            <p className="my-0 text-sm font-medium">Original Price: {formatCurrency(request.totalPrice)}</p>
            <p className="text-[#90A4AE] text-xs my-0">{dayjs(request.deliveryDate).format('DD/MM/YYYY')}</p>
            <div className="relative w-16 h-8">
              <img
                src={placeholder}
                alt="Material"
                className="absolute z-0 top-0 left-0 w-full h-full object-cover opacity-70 rounded"
              />
              <strong className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-semibold text-sm text-white z-20 border border-secondary capitalize">{request.material}</strong>
            </div>
            <div className="bg-[#F5F5F5] border-2 border-dashed border-blue-200 rounded p-2 mt-4">
              <div className="font-medium text-[#1A237E] text-sm underline cursor-pointer">
                {request.renterName}
              </div>
              <div className="text-blue-600 text-sm my-3">{request.renterPhone}</div>
              <div className="text-[#90A4AE] text-xs">{request?.renterEmail}</div>
              <div className="text-[#90A4AE] text-xs mt-1">On {dayjs(request.createdAt).format('DD/MM/YYYY')}</div>
            </div>
          </div>
          {/* Route Info */}
          <div className="flex-1 flex flex-col gap-6">
            <div>
              <h2 className="font-semibold text-base text-gray-500 my-4">Route</h2>
              <div className="flex flex-col gap-4">
                <div>
                  <h4 className="text-sm text-gray-500 mb-2">Loading Point</h4>
                  <div className="bg-[#F5F5F5] rounded-t px-4 py-2 font-semibold text-[#1A237E]">{request?.pickupZone?.title || request?.pickupAddress}</div>
                  <div className="bg-[#E3F2FD] rounded-b px-4 py-2 text-[#90A4AE] text-sm">{request?.pickupZone?.description}</div>
                </div>
                <div>
                  <h4 className="text-sm text-gray-500 mb-2">Dumping Point</h4>
                  <div className="bg-[#F5F5F5] rounded-t px-4 py-2 font-semibold text-[#1A237E]">{request?.dropoffZone?.title || request?.dropoffAddress}</div>
                  <div className="bg-[#E3F2FD] rounded-b px-4 py-2 text-[#90A4AE] text-sm">{request.dropoffZone?.description}</div>
                </div>
              </div>
            </div>
            {request.status === REQUEST_STATUS.ACCEPTED && (
              <div className="bg-[#F5F5F5] p-2 mt-4 text-sm text-gray-500">
                <h2 className="my-4 text-sm">Trip Completed</h2>
                <div className="space-y-2 text-xs">
                  {Array.from({ length: request.noOfTrips }, (v, i) => i).map((trip, index) => (<div key={index} className="flex items-center">
                    <span>Trip {index + 1}</span>
                    <span className="text-primary ml-auto mr-4">{dayjs(request.createdAt).format('DD/MM/YYYY @ hh:mm') ?? '-'}</span>
                  </div>))}
                </div>
              </div>
            )}
          </div>
        </div>
        {request.status === REQUEST_STATUS.ASSIGNED && (
          <div className="bg-[#F5F5F5] p-2 mt-4 text-sm text-gray-500">
            <h2 className="my-4 text-sm">Trip Assignment</h2>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {request.assignments.map((assignment, index) => (<div key={index}>
                <div className="flex items-center justify-between gap-2">
                  <span>{assignment?.fleetOwner?.companyName} {" "} ({assignment.tripsAssigned})</span>
                  <span className="text-primary mr-4">{dayjs(assignment.timeOfAssignment).format('DD/MM/YYYY @ hh:mm a') ?? '-'}</span>
                </div>
                {assignment.vehicles.map((vehicle) => (
                  <div key={vehicle.vehicle.id} className="bg-[#F5F5F5] border-2 border-dashed border-blue-200 rounded p-2 my-3 mx-2">
                    <p className="flex items-center justify-between">
                      <span>Diver Name:</span>
                      <strong className="text-black font-medium">{vehicle.vehicle.driverName}</strong>
                    </p>
                    <p className="flex items-center justify-between">
                      <span>Diver Phone:</span>
                      <strong className="text-black font-medium">{vehicle.vehicle.driverPhone}</strong>
                    </p>
                    <p className="flex items-center justify-between">
                      <span>Plate Number:</span>
                      <strong className="text-black font-medium">{vehicle.vehicle.regPlate}</strong>
                    </p>
                    <p className="flex items-center justify-between">
                      <span>No. Of Trip(s):</span>
                      <strong className="text-black font-medium">{vehicle.noOfTrips}</strong>
                    </p>
                    <p className="flex items-center justify-between">
                      <span>Completed Trip(s):</span>
                      <strong className="text-black font-medium">{vehicle.completedTrips}</strong>
                    </p>
                  </div>
                ))}
              </div>))}
            </div>
          </div>
        )}        
        {/* Statuses */}
        <div className="grid grid-cols-3 gap-4 mt-12">
            <div className="flex">
                <div>
                    <div className="text-[#90A4AE] font-bold text-xs">Assigned</div>
                    <div className="text-blue-500 font-medium capitalize">
                      {request.status !== REQUEST_STATUS.PENDING ? request?.assignedBy?.name : 'Pending'}
                    </div>
                    <div className="text-[#90A4AE] text-xs">
                      {request.assignedBy ? dayjs(request.assignedBy?.timeOfAssignment).format('DD/MM/YYYY @ hh:mm') : '-'}
                    </div>
                </div>
                <div className="flex-1 mt-2 h-[3px] bg-blue-200" />
            </div>
            <div className="flex">
                <div>
                    <div className="text-[#90A4AE] font-bold text-xs">Deployed</div>
                    <div className="text-blue-500 font-medium capitalize">{request?.status === REQUEST_STATUS.ACCEPTED ? 'Active' : 'Pending'}</div>
                    <div className="text-[#90A4AE] text-xs">-</div>
                </div>
                <div className="flex-1 mt-2 h-[3px] bg-blue-200" />
            </div>
            <div>
                <div className="text-[#90A4AE] font-bold text-xs">Completed</div>
                <div className="text-blue-500 font-medium capitalize">{request?.status === REQUEST_STATUS.COMPLETED ? 'Completed' : 'Pending'}</div>
                <div className="text-[#90A4AE] text-xs">-</div>
            </div>
        </div>
      </div>
      {/* Right Section */}
      <div className="w-full md:w-[340px] bg-white rounded-lg shadow p-4 flex flex-col gap-4">
        {(request.status === REQUEST_STATUS.ASSIGNED || request.status === REQUEST_STATUS.PENDING) && (
          <FleetOwners request={request} />
        )}
        {/* {request.status === REQUEST_STATUS.ASSIGNED && (
          <FleetOwners request={request} />
        )} */}
        {(request.status === REQUEST_STATUS.ACCEPTED || request.status === REQUEST_STATUS.COMPLETED) && (
          <FleetOwnersAssigned request={request}  />
        )}
      </div>
    </div>
  );
}
